import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Send } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { API } from "../context/AuthContext";
import { SKINS } from "../components/matrix/skins";
import { EventCard, Marker } from "../components/matrix/EventCard";

const inputCls = "w-full bg-white border border-[#d6cdb8] rounded-lg px-3 py-2 text-xs text-gray-800 focus:border-[#7c5e10] focus:outline-none";

function TipForm({ slug }) {
  const [form, setForm] = useState({ date: "", title: "", description: "", name: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Describe what you saw first.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/${slug}/tips`, form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit your tip. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-emerald-50 border border-emerald-300 rounded-2xl p-6 text-center space-y-1" data-testid="tip-thanks">
        <p className="text-sm font-bold text-emerald-800">Thank you. Your tip has been submitted.</p>
        <p className="text-xs text-emerald-700">The timeline admin will review it before it appears publicly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-[#d6cdb8] rounded-2xl p-6 space-y-3 shadow-sm" data-testid="tip-form">
      <h3 className="text-sm font-bold text-[#1f2937] font-serif uppercase tracking-wide">Submit a Tip</h3>
      <p className="text-[11px] text-[#4b5563]">Saw something relevant? Every detail helps. Tips are reviewed by the timeline admin before appearing.</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-[10px] font-mono text-[#7c5e10] uppercase">When (if known)</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="tip-date-input" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-mono text-[#7c5e10] uppercase">Your name (optional)</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} placeholder="Anonymous" className={inputCls} data-testid="tip-name-input" />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-[10px] font-mono text-[#7c5e10] uppercase">What did you see? *</label>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={150} placeholder="e.g., Saw them at the Main St gas station around 8pm" className={inputCls} data-testid="tip-title-input" />
      </div>
      <div className="space-y-1">
        <label className="text-[10px] font-mono text-[#7c5e10] uppercase">Details</label>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={600} rows={3} placeholder="Anything else — clothing, vehicle, who they were with..." className={inputCls} data-testid="tip-desc-input" />
      </div>
      <div className="space-y-1">
        <label className="text-[10px] font-mono text-[#7c5e10] uppercase">Contact info (optional, only visible to the admin)</label>
        <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} maxLength={120} placeholder="Phone or email if investigators may follow up" className={inputCls} data-testid="tip-contact-input" />
      </div>
      <button type="submit" disabled={busy} data-testid="tip-submit-btn" className="w-full flex items-center justify-center gap-2 bg-[#7c5e10] hover:bg-[#9a7a1d] text-white font-bold text-xs py-2.5 rounded-lg transition-colors duration-200 disabled:opacity-50">
        <Send className="w-3.5 h-3.5" /> {busy ? "Submitting..." : "Submit Tip"}
      </button>
    </form>
  );
}

export default function PublicTimeline() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const skin = SKINS.courtroom;

  useEffect(() => {
    axios.get(`${API}/public/${slug}`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || "This public timeline doesn't exist."));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f1e6] flex items-center justify-center p-6">
        <p className="text-sm text-[#4b5563]" data-testid="public-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#f5f1e6] flex items-center justify-center"><p className="text-xs font-mono text-[#7c5e10] animate-pulse">Loading public timeline...</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#f5f1e6] pb-20" data-testid="public-timeline-page">
      <header className="max-w-3xl mx-auto px-6 pt-12 pb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[#7c5e10]">
              <Globe className="w-4 h-4" />
              <span className="text-[10px] font-mono font-bold uppercase tracking-[0.2em]">Public Community Timeline · Flux Capacitor</span>
            </div>
            <h1 className="text-3xl font-bold text-[#1f2937] font-serif" data-testid="public-title">{data.title}</h1>
            {data.description && <p className="text-sm text-[#4b5563] leading-relaxed" data-testid="public-desc">{data.description}</p>}
            <p className="text-[11px] font-mono text-[#7c5e10]">{data.event_count} verified event{data.event_count === 1 ? "" : "s"} · tips reviewed by the timeline admin before publishing</p>
          </div>
          <div className="flex flex-col items-center gap-2 bg-white border border-[#d6cdb8] rounded-2xl p-4 shadow-sm shrink-0" data-testid="public-qr-block">
            <QRCodeCanvas value={window.location.href} size={110} fgColor="#1f2937" bgColor="#ffffff" />
            <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-[#7c5e10] text-center">Scan to submit<br />a tip</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 space-y-8">
        <TipForm slug={slug} />
        <div className={`border-l-2 pl-6 ml-3 space-y-4 ${skin.rail}`} data-testid="public-event-stream">
          {data.events.length === 0 ? (
            <p className="text-xs italic text-[#4b5563] py-8">No events published yet. Be the first to submit a tip.</p>
          ) : (
            data.events.map((ev) => (
              <div key={ev.id} className="relative">
                <div className="absolute -left-[33px] top-4 flex items-center justify-center w-4">
                  <Marker event={ev} />
                </div>
                <EventCard event={ev} skin={skin} onDelete={null} publicView publicSlug={slug} />
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
